import { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import * as Icons from "lucide-react";
import { Search, Mail } from "lucide-react";
import { getPublishedFaq, useQuery } from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import Footer from "../landing-page/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../client/components/ui/accordion";
import { Card, CardContent } from "../client/components/ui/card";
import { Badge } from "../client/components/ui/badge";
import { Button } from "../client/components/ui/button";
import { renderFaqMarkdown, stripMarkdown } from "./markdown";
import {
  filterFaq,
  itemQuestion,
  itemAnswer,
  categoryName,
  buildSnippet,
  type FaqCategoryPublic,
  type FaqLocale,
} from "./search";
import { buildFaqPageLd, serializeJsonLd, SITE_ORIGIN } from "../shared/seoJsonLd";

const CONTACT_EMAIL = "contact@edgely.trade";

// Render a Lucide icon by name (from the category `icon` field). Falls back to
// HelpCircle for unknown/absent names.
function CategoryIcon({ name, className }: { name: string | null; className?: string }) {
  const Cmp = (name && (Icons as any)[name]) || Icons.HelpCircle;
  return <Cmp className={className} />;
}

// A single FAQ answer, markdown → sanitized HTML.
function FaqAnswer({ markdown }: { markdown: string }) {
  const html = useMemo(() => renderFaqMarkdown(markdown), [markdown]);
  return (
    <div
      className="prose prose-sm max-w-none text-muted-foreground [&_a]:text-primary [&_a]:underline [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function HelpPage() {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const locale = language as FaqLocale;
  const { data, isLoading } = useQuery(getPublishedFaq, undefined);
  const categories: FaqCategoryPublic[] = (data as FaqCategoryPublic[]) ?? [];

  const [query, setQuery] = useState("");
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);

  // Deep-link: open + scroll to #slug on mount and on hash change.
  useEffect(() => {
    const applyHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (!hash) return;
      setQuery("");
      setOpenItem(hash);
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [categories.length]);

  // Autofocus the search box on mount.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const results = useMemo(
    () => filterFaq(categories, query, locale),
    [categories, query, locale],
  );
  const isSearching = query.trim().length > 0;

  const jsonLd = useMemo(() => {
    const items = categories.flatMap((c) =>
      c.items.map((it) => ({
        question: itemQuestion(it, locale),
        answer: stripMarkdown(itemAnswer(it, locale)),
      })),
    );
    return buildFaqPageLd(items);
  }, [categories, locale]);

  const scrollToCategory = (catId: string) => {
    const el = document.getElementById(`cat-${catId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openQuestion = (slug: string) => {
    setQuery("");
    setOpenItem(slug);
    window.history.replaceState(null, "", `#${slug}`);
    requestAnimationFrame(() => {
      document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Helmet>
        <title>{t.help.metaTitle}</title>
        <meta name="description" content={t.help.heroSubtitle} />
        <meta property="og:title" content={t.help.metaTitle} />
        <meta property="og:description" content={t.help.heroSubtitle} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${SITE_ORIGIN}/help`} />
        {jsonLd && (
          <script type="application/ld+json">{serializeJsonLd(jsonLd)}</script>
        )}
      </Helmet>

      <main className="isolate">
        <section className="mx-auto max-w-3xl px-6 pt-20 pb-10 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl font-display">
            {t.help.heroTitle}
          </h1>
          <p className="mt-4 text-muted-foreground">{t.help.heroSubtitle}</p>
          <div className="relative mt-8">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.help.searchPlaceholder}
              aria-label={t.help.searchPlaceholder}
              className="w-full rounded-xl border border-border bg-card py-4 pl-12 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </section>

        <div className="mx-auto max-w-4xl px-6 pb-16">
          {isLoading && <p className="text-center text-muted-foreground">…</p>}

          {!isLoading && isSearching && (
            <section aria-label="results">
              {results.length > 0 ? (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
                      {t.help.searchResultsTitle.replace("{count}", String(results.length))}
                    </p>
                    <button onClick={() => setQuery("")} className="text-sm text-primary hover:underline">
                      {t.help.backToCategories}
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {results.map((r) => (
                      <li key={r.item.id}>
                        <button
                          onClick={() => openQuestion(r.item.slug)}
                          className="block w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <span className="font-medium text-foreground">{r.question}</span>
                            <Badge variant="secondary" className="shrink-0">{r.categoryName}</Badge>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {buildSnippet(stripMarkdown(r.answerPlain), query)}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="rounded-xl border border-border bg-card p-8 text-center">
                  <p className="text-lg font-medium text-foreground">
                    {t.help.noResultsTitle.replace("{query}", query)}
                  </p>
                  <p className="mt-2 text-muted-foreground">{t.help.noResultsBody}</p>
                  <Button asChild className="mt-4">
                    <a href={`mailto:${CONTACT_EMAIL}`}>
                      <Mail className="mr-2 h-4 w-4" />
                      {t.help.contactCta}
                    </a>
                  </Button>
                </div>
              )}
            </section>
          )}

          {!isLoading && !isSearching && (
            <>
              <section aria-label="categories" className="mb-12">
                <h2 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {t.help.categoriesTitle}
                </h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {categories.map((cat) => (
                    <button key={cat.id} onClick={() => scrollToCategory(cat.id)} className="text-left">
                      <Card className="h-full transition-colors hover:border-primary">
                        <CardContent className="flex items-center gap-3 p-4">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <CategoryIcon name={cat.icon} className="h-5 w-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">{categoryName(cat, locale)}</p>
                            <p className="text-sm text-muted-foreground">
                              {t.help.questionsCount.replace("{count}", String(cat.items.length))}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
              </section>

              {categories.map((cat) => (
                <section key={cat.id} id={`cat-${cat.id}`} className="mb-10 scroll-mt-24" aria-label={categoryName(cat, locale)}>
                  <h2 className="mb-2 flex items-center gap-2 text-xl font-bold text-foreground">
                    <span aria-hidden className="h-2 w-2 rounded-full bg-primary" />
                    {categoryName(cat, locale)}
                  </h2>
                  <Accordion
                    type="single"
                    collapsible
                    value={openItem}
                    onValueChange={setOpenItem}
                    className="rounded-xl border border-border bg-card px-4"
                  >
                    {cat.items.map((it) => (
                      <AccordionItem key={it.id} value={it.slug} id={it.slug} className="scroll-mt-24">
                        <AccordionTrigger className="text-base">
                          <h3 className="text-left text-base font-medium">{itemQuestion(it, locale)}</h3>
                        </AccordionTrigger>
                        <AccordionContent>
                          <FaqAnswer markdown={itemAnswer(it, locale)} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>
              ))}

              <div className="mt-12 rounded-xl border border-border bg-card p-8 text-center">
                <p className="text-lg font-semibold text-foreground">{t.help.notFoundTitle}</p>
                <p className="mx-auto mt-2 max-w-md text-muted-foreground">{t.help.notFoundBody}</p>
                <Button asChild className="mt-4">
                  <a href={`mailto:${CONTACT_EMAIL}`}>
                    <Mail className="mr-2 h-4 w-4" />
                    {t.help.contactCta}
                  </a>
                </Button>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
